import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Avatar, MenuItem, TextField,
  Alert, CircularProgress, Grid, Chip, InputAdornment,
} from '@mui/material';
import { Timeline, Search } from '@mui/icons-material';
import EmployeeTimeline from '../../components/EmployeeTimeline';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

export default function EmployeeTimelinePage() {
  const { user } = useAuth();
  const isHR   = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const isMgr  = isHR || (user as any)?.role === 'manager';

  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedEmp, setSelectedEmp] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isMgr) {
      setLoading(true);
      apiFetch('/api/employees?limit=500')
        .then(d => setEmployees(Array.isArray(d) ? d : (d.data ?? [])))
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    } else {
      // Employees see their own timeline only
      const myEmpId = (user as any)?.employee?.id;
      if (myEmpId) setSelectedId(myEmpId);
    }
  }, [isMgr, user]);

  const filteredEmployees = employees.filter(e => {
    if (!search) return true;
    const name = `${e.firstName} ${e.lastName}`.toLowerCase();
    return name.includes(search.toLowerCase()) || e.department?.name?.toLowerCase().includes(search.toLowerCase());
  });

  const handleSelect = (empId: string) => {
    setSelectedId(empId);
    setSelectedEmp(employees.find(e => e.id === empId) || null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#C08A2E', width: 48, height: 48 }}><Timeline /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Employee Timeline</Typography>
          <Typography variant="body2" color="text.secondary">
            Full chronological history — hires, promotions, salary changes, reviews, training and more
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {isMgr ? (
        <Grid container spacing={3}>
          {/* Left: employee list */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 0, overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <TextField size="small" fullWidth placeholder="Search employee…" value={search}
                  onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
              ) : (
                <Box sx={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
                  {filteredEmployees.map(e => (
                    <Box key={e.id} onClick={() => handleSelect(e.id)}
                      sx={{
                        p: 1.5, cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                        bgcolor: selectedId === e.id ? '#fff3e0' : 'transparent',
                        borderLeft: selectedId === e.id ? '4px solid #C08A2E' : '4px solid transparent',
                        '&:hover': { bgcolor: '#fafafa' },
                      }}>
                      <Typography variant="body2" fontWeight={600}>{e.firstName} {e.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{e.department?.name} · {e.position?.title}</Typography>
                    </Box>
                  ))}
                  {filteredEmployees.length === 0 && (
                    <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                      <Typography variant="caption">No employees found.</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right: timeline */}
          <Grid size={{ xs: 12, md: 9 }}>
            {!selectedId ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.secondary' }}>
                <Timeline sx={{ fontSize: 64, opacity: 0.2, mb: 1 }} />
                <Typography>Select an employee to view their timeline</Typography>
              </Box>
            ) : (
              <Paper sx={{ p: 2.5 }}>
                {selectedEmp && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pb: 2, borderBottom: '1px solid #f0f0f0' }}>
                    <Avatar sx={{ bgcolor: '#C08A2E', width: 44, height: 44 }}>
                      {selectedEmp.firstName?.[0]}{selectedEmp.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{selectedEmp.firstName} {selectedEmp.lastName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedEmp.position?.title} · {selectedEmp.department?.name}
                      </Typography>
                    </Box>
                    {selectedEmp.startDate && (
                      <Chip size="small" label={`Joined ${selectedEmp.startDate?.slice(0, 10)}`}
                        sx={{ ml: 'auto', bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem' }} />
                    )}
                  </Box>
                )}
                <EmployeeTimeline employeeId={selectedId} />
              </Paper>
            )}
          </Grid>
        </Grid>
      ) : (
        /* Employee self-view */
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>My Journey</Typography>
          {selectedId && <EmployeeTimeline employeeId={selectedId} />}
        </Paper>
      )}
    </Box>
  );
}
